import { UpgradeSubscriptionModal } from '@components'

import { NavItemSeparator, notify, TableStaticActions, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../../../enums/notificationKeys'
import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useComponentId } from '../../../../../../../../hooks'
import { isAuthorized } from '../../../../../../../../utils/isAuthorized'
import { DepositInvoiceUnavailableModal } from '../../../../../../elements/DepositInvoiceUnavailableModal'
import { SendAsEmailModal } from '../../../../../../elements/SendAsEmailModal'
import { DownloadDocumentFileType } from '../../../../../../enums/downloadDocumentFileType'
import { InvoiceDocumentType } from '../../../../../../enums/invoiceDocumentType'
import { QuoteRelatedDraftInvoiceType } from '../../../../../../enums/quoteRelatedDraftInvoiceType'
import { QuoteStatus } from '../../../../../../enums/quoteStatus'
import { SendAsEmailModalVariant } from '../../../../../../enums/sendAsEmailModalVariant'
import { ZervantLocale } from '../../../../../../enums/zervantLocale'
import { useCanCreateFinalInvoice } from '../../../../../../hooks/quotes/useCanCreateFinalInvoice'
import { useConvertQuoteToDepositInvoice } from '../../../../../../hooks/quotes/useConvertQuoteToDepositInvoice'
import { useConvertQuoteToInvoice } from '../../../../../../hooks/quotes/useConvertQuoteToInvoice'
import { useSendQuoteAsEmail } from '../../../../../../hooks/quotes/useSendQuoteAsEmail'
import { useUpdateQuoteStatus } from '../../../../../../hooks/quotes/useUpdateQuoteStatus'
import { useDownloadInvoice } from '../../../../../../hooks/useDownloadInvoice'
import { useFetchQuote } from '../../../../../../hooks/useFetchQuote'
import { useGuardedAction } from '../../../../../../hooks/useGuardedAction'
import { Quote } from '../../../../../../types/quotes/quote'
import { SendAsEmailForm } from '../../../../../../types/sendAsEmailForm'
import { getQuoteDepositInvoice } from '../../../../../../utils/depositInvoices/getQuoteDepositInvoice'
import { getQuoteDepositInvoiceDraft } from '../../../../../../utils/depositInvoices/getQuoteDepositInvoiceDraft'
import { getQuoteFinalInvoiceDraft } from '../../../../../../utils/depositInvoices/getQuoteFinalInvoiceDraft'
import { getQuoteRegularInvoiceDraft } from '../../../../../../utils/depositInvoices/getQuoteRegularInvoiceDraft'
import { getDiscountModeTrackingValue } from '../../../../../../utils/getDiscountModeTrackingValue'
import { getQuoteDiscountMode } from '../../../../../../utils/getQuoteDiscountMode'
import { isQuoteStatus } from '../../../../../../utils/isQuoteStatus'
import { getBlockCreateDepositInvoiceActionModalId } from '../../../../../BlockCreateInvoiceActionModal/utils/getBlockCreateDepositInvoiceActionModalId'
import { getBlockCreateInvoiceActionModalId } from '../../../../../BlockCreateInvoiceActionModal/utils/getBlockCreateInvoiceActionModalId'
import { BlockCreateInvoiceActionModals } from '../../../../../BlockCreateInvoiceActionModals'
import { DeleteQuoteModal } from '../../../../../DeleteQuoteModal'
import { DepositInvoiceDetailsModal } from '../../../../../DepositInvoiceDetailsModal'
import { getDepositInvoiceDetailsModalProps } from '../../../../../DepositInvoiceDetailsModal/utils/getDepositInvoiceDetailsModalProps'
import { MarkQuoteAsAcceptedConfirmationModal } from '../../../../../MarkQuoteAsAcceptedConfirmationModal'
import { QuotesTableStaticActionType } from '../../../../enums/quotesTableStaticActionType'
import { getQuotesTableStaticActions } from '../../../../utils/getQuotesTableStaticActions'

interface QuotesTableStaticActionsProps {
  convertToFinalInvoiceUrl?: string
  convertToInvoiceUrl?: string
  documentLocale: ZervantLocale
  documentType: Quote['documentType']
  duplicateUrl: string
  editUrl: string
  estimateNumber: string | undefined
  externalContactId: string | undefined
  quoteStatus: QuoteStatus | undefined
  // TableData (the table library contract) requires id to be string, so the quote id
  // arrives here as a string. It is converted to number when passed to DeleteQuoteModal.
  quoteId: string
}

export const QuotesTableStaticActions = ({
  convertToFinalInvoiceUrl,
  convertToInvoiceUrl,
  documentLocale,
  documentType,
  duplicateUrl,
  editUrl,
  estimateNumber,
  externalContactId,
  quoteStatus,
  quoteId,
}: QuotesTableStaticActionsProps): ReactElement => {
  const { t } = useTranslation()
  const [isFetchEnabled, setIsFetchEnabled] = useState(false)

  const { organization } = useUserOrganization()

  const deleteModalId = `${quoteId}-delete-quote-confirmation`
  const depositInvoiceDetailsModalId = `${quoteId}-${ModalId.DepositInvoiceDetailsModal}`
  const convertToDepositInvoiceModalId = `${quoteId}-convert-quote-to-deposit-invoice-confirmation`
  const depositInvoiceUnavailableModalId = `${quoteId}-${ModalId.DepositInvoiceUnavailableModal}`
  const depositInvoiceUpgradeModalId = `${quoteId}-${ModalId.UpgradeSubscriptionModal}-deposit-invoice`
  const sendAsEmailModalId = `${quoteId}-send-as-email`
  const markAsAcceptedModalId = `${quoteId}-${ModalId.MarkQuoteAsAcceptedConfirmationModal}`
  const upgradeModalId = useComponentId(ModalId.UpgradeSubscriptionModal)

  const { open: openMarkAsAcceptedModal, close: closeMarkAsAcceptedModal } = useModal(markAsAcceptedModalId)
  const { open: openDeleteModal } = useModal(deleteModalId)
  const { open: openDepositInvoiceUpgradeModal } = useModal(depositInvoiceUpgradeModalId)
  const { open: openUpgradeModal } = useModal(upgradeModalId)
  const { open: openDepositInvoiceUnavailableModal } = useModal(depositInvoiceUnavailableModalId)
  const shouldOfferUpgradeForInvoice = isAuthorized(Scope.OfferConvertToInvoiceUpsell)
  const shouldOfferUpgradeForDepositOrFinalInvoice = isAuthorized(Scope.OfferConvertToDepositInvoiceUpsell)

  const {
    quote,
    isError: isQuoteFetchError,
    isFetching: isQuoteFetching,
  } = useFetchQuote(quoteId, { enabled: isFetchEnabled, retry: false })

  const existingDepositInvoice = getQuoteDepositInvoice(quote)
  const depositInvoiceId = existingDepositInvoice?.invoiceId
  const existingDepositInvoiceDraft = getQuoteDepositInvoiceDraft(quote)
  const existingFinalInvoiceDraft = getQuoteFinalInvoiceDraft(quote)
  const existingRegularInvoiceDraft = getQuoteRegularInvoiceDraft(quote)

  const blockCreateInvoiceActionModalId = getBlockCreateInvoiceActionModalId(quoteId)
  const blockCreateDepositInvoiceActionModalId = getBlockCreateDepositInvoiceActionModalId(quoteId)
  const { open: openBlockCreateInvoiceActionModal } = useModal(blockCreateInvoiceActionModalId)
  const { open: openBlockCreateDepositInvoiceActionModal } = useModal(blockCreateDepositInvoiceActionModalId)

  const resolvedContactId = externalContactId ?? quote?.customer?.externalContactId ?? undefined
  const resolvedDocumentLocale = quote?.locale ?? documentLocale

  let relatedDraftInvoiceType: QuoteRelatedDraftInvoiceType | undefined
  if (existingDepositInvoiceDraft) {
    relatedDraftInvoiceType = QuoteRelatedDraftInvoiceType.DepositInvoice
  } else if (existingRegularInvoiceDraft) {
    relatedDraftInvoiceType = QuoteRelatedDraftInvoiceType.Invoice
  }

  const isCurrencyDifferent = quote?.summary?.foreignCurrency !== organization?.baseCurrencyId

  const trackingDiscountMode = getDiscountModeTrackingValue(getQuoteDiscountMode(quote))

  const { onConvertClick: onConvertToInvoiceClick } = useConvertQuoteToInvoice({
    convertToInvoiceUrl,
    existingInvoiceDraft: existingRegularInvoiceDraft,
    onUpgradeClick: shouldOfferUpgradeForInvoice ? openUpgradeModal : undefined,
    trackingDiscountMode,
    trackingEventName: 'XXX - Invoicing Core - Quotes List - Convert to invoice quick action clicked',
    trackingOption: 'invoice',
  })
  const handleConvertToInvoiceClick = useGuardedAction({
    isBlocked: !!existingDepositInvoiceDraft,
    openBlockingModal: openBlockCreateInvoiceActionModal,
    proceed: onConvertToInvoiceClick,
  })
  const { canCreateFinalInvoice } = useCanCreateFinalInvoice({
    depositInvoiceId,
    existingFinalInvoiceDraft,
    modalId: blockCreateInvoiceActionModalId,
  })
  const { onConvertClick: onConvertToFinalInvoiceClick } = useConvertQuoteToInvoice({
    convertToInvoiceUrl: convertToFinalInvoiceUrl,
    onUpgradeClick: shouldOfferUpgradeForDepositOrFinalInvoice ? openDepositInvoiceUpgradeModal : undefined,
    trackingDiscountMode,
    trackingEventName: 'XXX - Invoicing Core - Quotes List - Convert to invoice quick action clicked',
    trackingOption: 'finalInvoice',
  })

  const { download, isDownloading } = useDownloadInvoice()
  const { updateQuoteStatus, isProcessing } = useUpdateQuoteStatus()

  const { open: openSendAsEmailModal, close: closeSendAsEmailModal } = useModal(sendAsEmailModalId)
  const effectiveQuoteStatus = quote?.status?.status ?? quoteStatus
  const depositModalProps = getDepositInvoiceDetailsModalProps(quote)

  const {
    onConfirmConvert: onConfirmConvertToDepositInvoice,
    onConvertClick: onConvertToDepositInvoiceClick,
    onDepositInvoiceDetailsClose,
    onDepositInvoiceDetailsConfirm,
  } = useConvertQuoteToDepositInvoice({
    confirmationModalId: convertToDepositInvoiceModalId,
    depositInvoiceDetailsModalId,
    existingDepositInvoice,
    onUpgradeClick: shouldOfferUpgradeForDepositOrFinalInvoice ? openDepositInvoiceUpgradeModal : undefined,
    quoteId: quote?.id,
    quoteStatus: effectiveQuoteStatus,
    trackingDiscountMode,
    trackingEventName: 'XXX - Invoicing Core - Quotes List - Convert to invoice quick action clicked',
  })
  const handleConvertToDepositInvoiceClick = useGuardedAction({
    isBlocked: !!existingRegularInvoiceDraft,
    openBlockingModal: openBlockCreateDepositInvoiceActionModal,
    proceed: onConvertToDepositInvoiceClick,
  })

  const isDraft = documentType === InvoiceDocumentType.QuoteDraft
  const isLoading = !isDraft && !isQuoteFetchError && (!isFetchEnabled || !quote || isQuoteFetching)
  const handleMenuOpen = useCallback(() => {
    if (!isDraft) {
      setIsFetchEnabled(true)
    }
  }, [isDraft])

  const { sendAsEmail, isProcessing: isProcessingSendAsEmail } = useSendQuoteAsEmail({
    onSuccess: closeSendAsEmailModal,
  })

  const menuItems = useMemo(() => {
    if (isLoading) {
      return []
    }

    return getQuotesTableStaticActions({
      duplicateUrl,
      editUrl,
      isDraft,
      quoteStatus,
      t,
      fetchedQuoteEvents: quote?.status?.events ?? [],
      fetchedQuoteInvoiceState: quote?.invoiceState,
      fetchedQuoteStatus: quote?.status?.status,
      organizationUrl: organization?.url,
      isDownloading,
      isProcessing,
      isProcessingSendAsEmail,
    })
  }, [
    duplicateUrl,
    editUrl,
    isDraft,
    quoteStatus,
    t,
    quote?.invoiceState,
    quote?.status?.events,
    quote?.status?.status,
    organization?.url,
    isDownloading,
    isLoading,
    isProcessing,
    isProcessingSendAsEmail,
  ])

  const handleSendAsEmail = useCallback(() => {
    if (!resolvedContactId && isQuoteFetchError) {
      notify({ id: NotificationKeys.QuoteFetchFailed, message: t('something_went_wrong'), variant: 'error' })
      return
    }
    openSendAsEmailModal()
  }, [resolvedContactId, isQuoteFetchError, openSendAsEmailModal, t])

  const handleStaticActionSelect = useCallback(
    async (_id: string, value: QuotesTableStaticActionType | QuoteStatus | NavItemSeparator) => {
      if (isQuoteStatus(value)) {
        if (value === QuoteStatus.Accepted) {
          openMarkAsAcceptedModal()
          return
        }
        updateQuoteStatus({ quoteId, value })
        return
      }

      switch (value) {
        // Navigation is handled by the `to` prop on these items
        case QuotesTableStaticActionType.Edit:
        case QuotesTableStaticActionType.Duplicate:
        case QuotesTableStaticActionType.ViewLinkedInvoice:
          break

        case QuotesTableStaticActionType.ChangeStatus:
          // NavListItem fires onSelect for the parent item even when it has subItems
          // (the submenu expand/collapse is handled separately via expandedIds state).
          // No action needed here; the subItem click is what triggers the status update.
          break

        case QuotesTableStaticActionType.ConvertToInvoice:
          handleConvertToInvoiceClick()
          break

        case QuotesTableStaticActionType.ConvertToFinalInvoice:
          if (await canCreateFinalInvoice()) {
            onConvertToFinalInvoiceClick()
          }
          break

        case QuotesTableStaticActionType.ConvertToDepositInvoice:
          if (isCurrencyDifferent) {
            openDepositInvoiceUnavailableModal()
          } else {
            handleConvertToDepositInvoiceClick()
          }
          break

        case QuotesTableStaticActionType.Download:
          download({
            documentType: InvoiceDocumentType.Quote,
            documentLocale: resolvedDocumentLocale,
            downloadDocumentFileType: DownloadDocumentFileType.PDF,
            invoiceId: quoteId,
            invoiceNumber: estimateNumber,
          })
          break

        case QuotesTableStaticActionType.MarkAsSent:
          updateQuoteStatus({ quoteId, value: QuoteStatus.Sent })
          break

        case QuotesTableStaticActionType.MarkAsUnsent:
          updateQuoteStatus({ quoteId, value: QuoteStatus.Unsent })
          break

        case QuotesTableStaticActionType.SendByEmail:
          handleSendAsEmail()
          break

        case QuotesTableStaticActionType.Delete:
          openDeleteModal()
          break
      }
    },
    [
      canCreateFinalInvoice,
      download,
      estimateNumber,
      handleConvertToDepositInvoiceClick,
      handleConvertToInvoiceClick,
      isCurrencyDifferent,
      handleSendAsEmail,
      onConvertToFinalInvoiceClick,
      openDepositInvoiceUnavailableModal,
      openDeleteModal,
      openMarkAsAcceptedModal,
      quoteId,
      resolvedDocumentLocale,
      updateQuoteStatus,
    ],
  )

  const handleSendAsEmailSubmit = useCallback(
    (formData: SendAsEmailForm) => {
      sendAsEmail({
        documentType: InvoiceDocumentType.Quote,
        formData,
        invoiceId: Number(quoteId),
      })
    },
    [sendAsEmail, quoteId],
  )

  const handleConfirmMarkAsAccepted = useCallback(() => {
    updateQuoteStatus({ quoteId, value: QuoteStatus.Accepted })
    closeMarkAsAcceptedModal()
  }, [quoteId, updateQuoteStatus, closeMarkAsAcceptedModal])

  return (
    <>
      <TableStaticActions
        dropdownFetching={isQuoteFetching}
        items={menuItems}
        onOpen={handleMenuOpen}
        onSelect={handleStaticActionSelect}
      />
      <MarkQuoteAsAcceptedConfirmationModal
        id={convertToDepositInvoiceModalId}
        onConfirm={onConfirmConvertToDepositInvoice}
        trackingOption="depositInvoice"
      />
      <MarkQuoteAsAcceptedConfirmationModal id={markAsAcceptedModalId} onConfirm={handleConfirmMarkAsAccepted} />
      <DeleteQuoteModal
        id={deleteModalId}
        quoteDocumentType={documentType}
        quoteId={Number(quoteId)}
        relatedDraftInvoiceType={relatedDraftInvoiceType}
      />
      <UpgradeSubscriptionModal
        id={depositInvoiceUpgradeModalId}
        trackingContext={TrackingContext.DepositInvoiceUpsellQuotesList}
      />
      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.QuoteConvertToInvoice} />
      <BlockCreateInvoiceActionModals
        createInvoiceUrl={convertToInvoiceUrl}
        onCreateDepositInvoiceClick={onConvertToDepositInvoiceClick}
        quote={quote}
        scopeId={quoteId}
      />
      <SendAsEmailModal
        contactId={resolvedContactId}
        documentLocale={resolvedDocumentLocale}
        documentType={InvoiceDocumentType.Quote}
        id={sendAsEmailModalId}
        invoiceNumber={estimateNumber ?? ''}
        isSubmitting={isProcessingSendAsEmail}
        onSubmit={handleSendAsEmailSubmit}
        variant={SendAsEmailModalVariant.Invoice}
        view="quotesList"
      />

      <DepositInvoiceDetailsModal
        {...depositModalProps}
        id={depositInvoiceDetailsModalId}
        onClose={onDepositInvoiceDetailsClose}
        onConfirm={onDepositInvoiceDetailsConfirm}
      />

      <DepositInvoiceUnavailableModal
        id={depositInvoiceUnavailableModalId}
        onCreateRegularInvoice={handleConvertToInvoiceClick}
      />
    </>
  )
}
